import React, { useState, useRef, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import downloadIcon from '../../src/assets/download.svg';
import api from '../api/api';
import { Backdrop, CircularProgress } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { addDays } from 'date-fns';

dayjs.extend(isoWeek);

const RecapHistory = () => {
  const [dateRange, setDateRange] = useState([
    {
      startDate: dayjs().startOf('isoWeek').toDate(),
      endDate: dayjs().endOf('isoWeek').toDate(),
      key: 'selection',
    },
  ]);

  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  const [recapData, setRecapData] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const fetchhistorydata = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      const start = dayjs(startDate).format('YYYY-MM-DD');
      const end = dayjs(endDate).format('YYYY-MM-DD');
      const response = await api.get(`/v1/timesheets/csv/all?start_date=${start}&end_date=${end}`);
      if (response.data && Array.isArray(response.data)) {
        setRecapData(response.data);
      } else {
        setRecapData([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setRecapData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      fetchhistorydata(startDate, endDate);
    }
  }, [dateRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const handleSelect = (ranges) => {
    setDateRange([ranges.selection]);
    setShowCalendar(false);
    setCurrentPage(1);
  };

  const handlepdf = async (name) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/v1/timesheets/csv/download/${name}`, {
        responseType: 'blob',
      });
      const file = new Blob([response], { type: 'text/csv' });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `${name}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('CSV download error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalEntries = recapData.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const paginatedData = recapData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const { startDate, endDate } = dateRange[0];

  return (
    <div className="relative flex flex-col min-h-screen">
      {/* Date Picker Trigger */}
      <div className="flex justify-end mb-4 pr-4 relative">
        <div
          className="flex items-center gap-2 bg-white px-4 py-2 border rounded shadow cursor-pointer min-w-[220px]"
          onClick={() => setShowCalendar(!showCalendar)}
        >
          <CalendarMonthIcon />
          <span className="text-sm text-[#293050] font-Font1">
            {`${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(endDate).format('MMM DD, YYYY')}`}
          </span>
        </div>

        {showCalendar && (
          <div
            ref={calendarRef}
            className="absolute top-12 right-0 z-50 bg-white rounded shadow-xl"
          >
            <DateRangePicker
              ranges={dateRange}
              onChange={handleSelect}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              maxDate={new Date()}
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-grow overflow-auto mx-4 border border-gray-200 rounded-md">
        <table className="min-w-full bg-white text-sm font-inter">
          <thead>
            <tr className="bg-[#F9FAFB] text-[#101828] text-left">
              <th className="px-4 py-3 font-semibold">Created By</th>
              <th className="px-4 py-3 font-semibold">Supervisor ID</th>
              <th className="px-4 py-3 font-semibold">Date Ordered</th>
              <th className="px-4 py-3 font-semibold">Period of Report</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index} className="border-b text-start text-[#101828]">
                <td className="px-4 py-3">{item.created_by || '-'}</td>
                <td className="px-4 py-3">{item.supervisorid || '-'}</td>
                <td className="px-4 py-3">
                  {item.created_at ? dayjs(item.created_at).format('MMM D, YYYY') : '-'}
                </td>
                <td className="px-4 py-3">
                  {item.startdate && item.enddate
                    ? `${dayjs(item.startdate).format('MMM D, YYYY')} - ${dayjs(item.enddate).format('MMM D, YYYY')}`
                    : '-'}
                </td>
                <td className="px-4 py-3">
                  <div
                    onClick={() => handlepdf(item.filename)}
                    className="rounded border border-[#E2E8F0] cursor-pointer p-2 flex items-center justify-center w-fit"
                  >
                    <img src={downloadIcon} alt="download" className="h-5 w-5" />
                  </div>
                </td>
              </tr>
            ))}
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-6 text-gray-500">
                  No data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col md:flex-row justify-between items-center mt-4 mx-4 gap-4">
        <div className="flex items-center gap-2 text-sm font-inter">
          <span>
            Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
            {Math.min(currentPage * entriesPerPage, totalEntries)} of {totalEntries} entries
          </span>
          <label htmlFor="entriesPerPage" className="ml-4">
            Rows per page:
          </label>
          <select
            id="entriesPerPage"
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md ${
              currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-[#900] text-white'
            }`}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md ${
                currentPage === page ? 'bg-[#900] text-white' : 'bg-gray-200'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md ${
              currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-[#900] text-white'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Loader */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isloading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default RecapHistory;
